import React, { JSX, ReactNode, createContext, useContext, useMemo, useState } from 'react'
import { ChatMessage, ChatPlatform } from 'lib/twitchChatParse'
import { previewSeamActive } from 'components/overlayPreview/previewSeam'

export const CHAT_LINE_REMOVE_LABEL = 'Remove this line from the overlays'
export const CHAT_LINE_REMOVE_TOOLTIP_STREAM = "Removes this line from this stream's overlays and the host's Quick Overlays. It is not deleted on Twitch or YouTube."
export const CHAT_LINE_REMOVE_TOOLTIP_QUICK = 'Removes this line from your Quick Overlays. It is not deleted on Twitch or YouTube.'
export const CHAT_LINE_REMOVE_FAILED = 'Could not remove this line. Try again.'
export const CHAT_LINE_REMOVE_RETRY = 'Retry'

export const CHAT_LINE_RESEND_MS = 5000

export interface ChatLineRemoval {
  chatId: string
  eventId: string | null
  platform: ChatPlatform
  messageId: string
}

export interface ChatLineRemovePaneValue {
  eventId: string | null
  remove: (removal: ChatLineRemoval) => void
}

const PaneContext = createContext<ChatLineRemovePaneValue | null>(null)

export const ChatLineRemovePane = ({
  eventId,
  onRemove,
  offered = true,
  children
}: {
  eventId: string | null
  onRemove: (removal: ChatLineRemoval) => void
  offered?: boolean
  children: ReactNode
}): JSX.Element => {
  const value = useMemo(() => (offered ? { eventId, remove: onRemove } : null), [offered, eventId, onRemove])
  return <PaneContext.Provider value={value}>{children}</PaneContext.Provider>
}

export const useChatLineRemovePane = (): ChatLineRemovePaneValue | null => useContext(PaneContext)

export const chatLineRemoveOn = (pane: ChatLineRemovePaneValue | null, seam: unknown): boolean => {
  if (pane == null) return false
  if (!previewSeamActive(seam)) return false
  if (seam.mode !== 'live') return false
  if ((globalThis as unknown as { obsstudio?: unknown }).obsstudio != null) return false
  return true
}

export const useChatLineRemoveOn = (seam: unknown): boolean =>
  chatLineRemoveOn(useContext(PaneContext), seam)

const TWITCH_MESSAGE_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const YOUTUBE_MESSAGE_ID = /^[A-Za-z0-9._~+/=-]{1,256}$/

export const removableIdOf = (message: Pick<ChatMessage, 'id' | 'platform'>): string | null => {
  const id = message.id
  if (typeof id !== 'string' || id.startsWith('sample-')) return null
  if (message.platform === 'TWITCH') return TWITCH_MESSAGE_ID.test(id) ? id : null
  if (message.platform === 'YOUTUBE') return YOUTUBE_MESSAGE_ID.test(id) ? id : null
  return null
}

export const chatLineRemoveClick = (
  e: { preventDefault: () => void, stopPropagation: () => void },
  press: () => void
): void => {
  e.preventDefault()
  e.stopPropagation()
  press()
}

export interface ChatLineRemover {
  remove: (removal: ChatLineRemoval) => void
  retry: () => void
  dispose: () => void
}

export const createChatLineRemover = ({
  send,
  onFailure,
  onSuccess = () => {},
  resendMs = CHAT_LINE_RESEND_MS
}: {
  send: (removal: ChatLineRemoval) => Promise<unknown>
  onFailure: (removal: ChatLineRemoval) => void
  onSuccess?: (removal: ChatLineRemoval) => void
  resendMs?: number
}): ChatLineRemover => {
  let disposed = false
  let lastFailed: ChatLineRemoval | null = null
  const timers = new Set<ReturnType<typeof globalThis.setTimeout>>()
  const remove = (removal: ChatLineRemoval): void => {
    if (disposed) return
    send(removal).then(() => {
      if (disposed) return
      if (lastFailed === removal) lastFailed = null
      if (lastFailed == null) onSuccess(removal)
      const t = globalThis.setTimeout(() => {
        timers.delete(t)
        if (disposed) return
        send(removal).catch(() => {})
      }, resendMs)
      timers.add(t)
    }).catch(() => {
      if (disposed) return
      lastFailed = removal
      onFailure(removal)
    })
  }
  return {
    remove,
    retry: () => {
      if (lastFailed != null) remove(lastFailed)
    },
    dispose: () => {
      disposed = true
      timers.forEach((t) => globalThis.clearTimeout(t))
      timers.clear()
    }
  }
}

export const removeChatLineVariablesOf = (removal: ChatLineRemoval): { input: Record<string, string> } => ({
  input: {
    chatId: removal.chatId,
    ...(removal.eventId != null && { eventId: removal.eventId }),
    platform: removal.platform,
    messageId: removal.messageId
  }
})

export const CHAT_LINE_REMOVE_BUTTON_STYLE: React.CSSProperties = Object.freeze({
  position: 'absolute',
  right: 0,
  top: '50%',
  transform: 'translateY(-50%)',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 20,
  height: 20,
  padding: 0,
  margin: 0,
  border: 'none',
  borderRadius: 10,
  background: 'rgba(0, 0, 0, 0.75)',
  color: '#ffffff',
  cursor: 'pointer',
  pointerEvents: 'auto'
})

export const counterScaleOf = (drawnWidth: number, layoutWidth: number): number => {
  if (!(drawnWidth > 0) || !(layoutWidth > 0)) return 1
  const scale = drawnWidth / layoutWidth
  return Math.min(Math.max(1 / scale, 0.25), 4)
}

export const ChatLineRemoveButton = ({
  visible,
  tooltip,
  onPress,
  counterScale = 1
}: {
  visible: boolean
  tooltip: string
  onPress: () => void
  counterScale?: number
}): JSX.Element => (
  <button
    type='button'
    aria-label={CHAT_LINE_REMOVE_LABEL}
    title={tooltip}
    data-testid='chat-line-remove'
    style={{
      ...CHAT_LINE_REMOVE_BUTTON_STYLE,
      opacity: visible ? 1 : 0,
      ...(counterScale !== 1 && { transform: `translateY(-50%) scale(${counterScale})`, transformOrigin: 'right center' })
    }}
    onClick={(e) => chatLineRemoveClick(e, onPress)}
    onPointerDown={(e) => e.stopPropagation()}
  >
    <svg width='10' height='10' viewBox='0 0 10 10' aria-hidden='true' focusable='false'>
      <path d='M1 1 L9 9 M9 1 L1 9' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' />
    </svg>
  </button>
)

export const RemovableChatLine = ({
  style,
  message,
  tooltip,
  onRemove,
  children
}: {
  style: React.CSSProperties
  message: Pick<ChatMessage, 'id' | 'platform'>
  tooltip: string
  onRemove: (platform: ChatPlatform, messageId: string) => void
  children: ReactNode
}): JSX.Element => {
  const [shown, setShown] = useState(false)
  const [counterScale, setCounterScale] = useState(1)
  const id = removableIdOf(message)
  if (id == null) return <div style={style}>{children}</div>
  const show = (el: HTMLElement): void => {
    setCounterScale(counterScaleOf(el.getBoundingClientRect().width, el.offsetWidth))
    setShown(true)
  }
  return (
    <div
      style={{ ...style, position: 'relative', pointerEvents: 'auto' }}
      data-testid='chat-line-removable'
      onMouseEnter={(e) => show(e.currentTarget)}
      onMouseLeave={() => setShown(false)}
      onFocus={(e) => show(e.currentTarget)}
      onBlur={() => setShown(false)}
    >
      {children}
      <ChatLineRemoveButton visible={shown} tooltip={tooltip} counterScale={counterScale} onPress={() => onRemove(message.platform, id)} />
    </div>
  )
}
